import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  OutlinedInput,
  Tabs,
  Tab,
  Avatar,
  Chip
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Save,
  Edit,
  Cancel
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const UserProfile = () => {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: ''
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileForm({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        email: user.email || '',
        username: user.username || ''
      });
    }
  }, [user]);

  const handleProfileChange = (field) => (event) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handlePasswordChange = (field) => (event) => {
    setPasswordForm(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleShowPassword = (field) => () => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setMessage('');
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      errors: [
        password.length < minLength && `Password must be at least ${minLength} characters long`,
        !hasUpperCase && 'Password must contain at least one uppercase letter',
        !hasLowerCase && 'Password must contain at least one lowercase letter',
        !hasNumbers && 'Password must contain at least one number',
        !hasSpecialChar && 'Password must contain at least one special character'
      ].filter(Boolean)
    };
  };

  const handleProfileSave = async () => {
    setSaving(true);
    setError('');

    try {
      const response = await axios.put('/api/auth/profile', {
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        email: profileForm.email
      });

      // Update user context with new data
      setUser(prev => ({
        ...prev,
        first_name: response.data.user.first_name,
        last_name: response.data.user.last_name,
        email: response.data.user.email
      }));

      setMessage(t('auth.profileUpdated'));
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.response?.data?.error || t('auth.profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSave = async () => {
    setSaving(true);
    setError('');

    // Validate passwords
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      setSaving(false);
      return;
    }

    const passwordValidation = validatePassword(passwordForm.newPassword);
    if (!passwordValidation.isValid) {
      setError(`Password requirements not met: ${passwordValidation.errors.join(', ')}`);
      setSaving(false);
      return;
    }

    try {
      await axios.put('/api/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      });

      // Clear password form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setMessage(t('auth.passwordChanged'));
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.response?.data?.error || t('auth.passwordChangeError'));
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return first + last;
  };

  const getUserTypeColor = (userType) => {
    switch (userType) {
      case 'admin': return 'error';
      case 'manager': return 'warning';
      case 'scheduler': return 'info';
      case 'field_worker': return 'success';
      case 'office': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        {t('auth.profileTitle')}
      </Typography>

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: 'primary.main',
                mr: 2,
                fontSize: '1.5rem'
              }}
            >
              {getInitials(profileForm.firstName, profileForm.lastName)}
            </Avatar>
            <Box>
              <Typography variant="h5">
                {profileForm.firstName} {profileForm.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                @{profileForm.username}
              </Typography>
              <Chip
                label={user?.user_type || 'User'}
                color={getUserTypeColor(user?.user_type)}
                size="small"
                sx={{ mt: 1 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<Person />}
            label={t('auth.profileInformation')}
            iconPosition="start"
          />
          <Tab
            icon={<Lock />}
            label={t('auth.changePassword')}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Profile Information Tab */}
      {activeTab === 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('auth.profileInformation')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.updatePersonalInfo')}
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('auth.firstName')}
                value={profileForm.firstName}
                onChange={handleProfileChange('firstName')}
                variant="outlined"
                disabled={saving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('auth.lastName')}
                value={profileForm.lastName}
                onChange={handleProfileChange('lastName')}
                variant="outlined"
                disabled={saving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('auth.email')}
                type="email"
                value={profileForm.email}
                onChange={handleProfileChange('email')}
                variant="outlined"
                disabled={saving}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label={t('auth.username')}
                value={profileForm.username}
                variant="outlined"
                disabled
                helperText={t('auth.usernameCannotBeChanged')}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Save />}
              onClick={handleProfileSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : t('common.save')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Change Password Tab */}
      {activeTab === 1 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('auth.changePassword')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('auth.updatePassword')}
          </Typography>

          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('auth.currentPassword')}</InputLabel>
                <OutlinedInput
                  type={showPassword.current ? 'text' : 'password'}
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange('currentPassword')}
                  disabled={saving}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleShowPassword('current')}
                        edge="end"
                        disabled={saving}
                      >
                        {showPassword.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t('auth.currentPassword')}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('auth.newPassword')}</InputLabel>
                <OutlinedInput
                  type={showPassword.new ? 'text' : 'password'}
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange('newPassword')}
                  disabled={saving}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleShowPassword('new')}
                        edge="end"
                        disabled={saving}
                      >
                        {showPassword.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t('auth.newPassword')}
                />
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>{t('auth.confirmPassword')}</InputLabel>
                <OutlinedInput
                  type={showPassword.confirm ? 'text' : 'password'}
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange('confirmPassword')}
                  disabled={saving}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleShowPassword('confirm')}
                        edge="end"
                        disabled={saving}
                      >
                        {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t('auth.confirmPassword')}
                />
              </FormControl>
            </Grid>
          </Grid>

          {/* Password Requirements */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t('auth.passwordRequirements')}:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • At least 8 characters long<br />
              • Contains uppercase and lowercase letters<br />
              • Contains numbers and special characters
            </Typography>
          </Box>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <Lock />}
              onClick={handlePasswordSave}
              disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword}
            >
              {saving ? 'Changing...' : t('auth.changePassword')}
            </Button>
          </Box>
        </Paper>
      )}

      {/* Messages */}
      <Snackbar
        open={!!message}
        autoHideDuration={6000}
        onClose={() => setMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setMessage('')} severity="success">
          {message}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error">
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserProfile;
